import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from 'react';
import type {
  AdminAccountListItem,
  AdminAccountListResponse,
  AdminUserRole,
} from '@shared/types';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import Pagination from '../../components/Pagination';
import ConfirmModal from '../../components/ConfirmModal';
import {
  listAdminAccounts,
  createAdminAccount,
  updateAdminAccount,
  resetAdminAccountPassword,
  resetAdminAccountMfa,
  deleteAdminAccount,
  ADMIN_ACCOUNT_ROLES,
  ADMIN_ROLE_LABELS,
} from '../../services/adminAccounts';
import { ApiError } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;
const ADMIN_PASSWORD_MIN_LENGTH = 8;

type ActionKind =
  | 'create'
  | 'edit'
  | 'resetPassword'
  | 'resetMfa'
  | 'toggleDisabled'
  | 'delete'
  | null;

interface ActionState {
  kind: ActionKind;
  target: AdminAccountListItem | null;
  busy: boolean;
  error: string | null;
}

const INITIAL_ACTION: ActionState = {
  kind: null,
  target: null,
  busy: false,
  error: null,
};

/**
 * Admin Users management page — superadmin only. Operates EXCLUSIVELY on the
 * AdminUser collection via `/api/admin/admin-users/*`. This page never lists
 * or mutates player records; see `routes/users/List.tsx` for the players
 * admin surface. The strict separation matches the server-side split and
 * prevents accidental cross-mutation.
 */
export default function AdminUsersList() {
  const actor = useAuthStore((s) => s.adminUser);

  const [page, setPage] = useState(1);
  const [searchRaw, setSearchRaw] = useState('');
  const [search, setSearch] = useState('');

  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'ready'; data: AdminAccountListResponse }
    | { status: 'error'; message: string }
  >({ status: 'loading' });

  const [action, setAction] = useState<ActionState>(INITIAL_ACTION);

  // Debounce search input to avoid hitting the API on every keystroke.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setSearch(searchRaw.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchRaw]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const reqCounterRef = useRef(0);
  const fetchAdmins = useCallback(async () => {
    const reqId = ++reqCounterRef.current;
    setState({ status: 'loading' });
    try {
      const data = await listAdminAccounts({
        page,
        limit: PAGE_SIZE,
        q: search || undefined,
      });
      if (reqId !== reqCounterRef.current) return;
      setState({ status: 'ready', data });
    } catch (err) {
      if (reqId !== reqCounterRef.current) return;
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to load admin users';
      setState({ status: 'error', message });
    }
  }, [page, search]);

  useEffect(() => {
    void fetchAdmins();
  }, [fetchAdmins]);

  const rows = useMemo<AdminAccountListItem[]>(() => {
    return state.status === 'ready' ? state.data.data : [];
  }, [state]);

  const totalPages = useMemo(() => {
    if (state.status !== 'ready') return 1;
    if (state.data.total === 0) return 1;
    return Math.max(1, Math.ceil(state.data.total / state.data.limit));
  }, [state]);

  const closeAction = useCallback(() => {
    setAction(INITIAL_ACTION);
  }, []);

  async function runAction<T>(
    fn: () => Promise<T>,
    after: () => void = () => {},
  ): Promise<void> {
    setAction((a) => ({ ...a, busy: true, error: null }));
    try {
      await fn();
      after();
      closeAction();
      void fetchAdmins();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Action failed';
      setAction((a) => ({ ...a, busy: false, error: message }));
    }
  }

  const columns: DataTableColumn<AdminAccountListItem>[] = useMemo(
    () => [
      {
        key: 'username',
        header: 'Username',
        render: (u) => (
          <span className="font-medium text-slate-900">
            {u.username}
            {actor && u.id === actor.id && (
              <span
                className="ml-2 text-[10px] uppercase tracking-wide text-slate-500"
                data-testid="admin-users-you"
              >
                (you)
              </span>
            )}
          </span>
        ),
      },
      {
        key: 'displayName',
        header: 'Display name',
        render: (u) => <span className="text-slate-700">{u.displayName}</span>,
      },
      {
        key: 'role',
        header: 'Role',
        render: (u) => (
          <span className="inline-block text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700 uppercase tracking-wide">
            {u.role}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (u) =>
          u.isDisabled ? (
            <span className="inline-block text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">
              Disabled
            </span>
          ) : (
            <span className="inline-block text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">
              Active
            </span>
          ),
      },
      {
        key: 'mfaEnrolled',
        header: 'MFA',
        render: (u) => (
          <span className="text-xs text-slate-600">
            {u.mfaEnrolled ? 'Enrolled' : 'Not enrolled'}
          </span>
        ),
      },
      {
        key: 'lastLoginAt',
        header: 'Last login',
        render: (u) => (
          <span className="text-xs text-slate-500">
            {u.lastLoginAt ? formatDate(u.lastLoginAt) : '—'}
          </span>
        ),
      },
      {
        key: 'actions',
        header: 'Actions',
        render: (u) => <RowActions actor={actor} row={u} onAction={setAction} />,
      },
    ],
    [actor],
  );

  return (
    <div className="space-y-4" data-testid="admin-users-list-screen">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-slate-900">Admin Users</h1>
        <button
          type="button"
          data-testid="admin-users-create"
          onClick={() =>
            setAction({ kind: 'create', target: null, busy: false, error: null })
          }
          className="text-sm rounded bg-slate-800 text-white px-3 py-1.5 hover:bg-slate-700"
        >
          Add admin
        </button>
      </div>

      <div className="bg-white rounded border border-slate-200 p-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs text-slate-600 flex-1 min-w-[200px]">
          Search
          <input
            type="text"
            value={searchRaw}
            onChange={(e) => setSearchRaw(e.target.value)}
            placeholder="Username or display name…"
            data-testid="admin-users-search-input"
            className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
          />
        </label>
      </div>

      {state.status === 'error' && (
        <div
          role="alert"
          data-testid="admin-users-list-error"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2"
        >
          {state.message}
        </div>
      )}

      <DataTable<AdminAccountListItem>
        columns={columns}
        rows={rows}
        rowKey={(u) => u.id}
        loading={state.status === 'loading'}
        empty={
          search ? 'No admin users match that search.' : 'No admin users yet.'
        }
        testId="admin-users-table"
      />

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        disabled={state.status === 'loading'}
      />

      {action.kind === 'create' && (
        <CreateAdminModal
          busy={action.busy}
          error={action.error}
          onCancel={closeAction}
          onSubmit={(body) => runAction(() => createAdminAccount(body))}
        />
      )}

      {action.kind === 'edit' && action.target && (
        <EditAdminModal
          target={action.target}
          busy={action.busy}
          error={action.error}
          onCancel={closeAction}
          onSubmit={(patch) =>
            runAction(() => updateAdminAccount(action.target!.id, patch))
          }
        />
      )}

      {action.kind === 'resetPassword' && action.target && (
        <ResetPasswordModal
          target={action.target}
          busy={action.busy}
          error={action.error}
          onCancel={closeAction}
          onSubmit={(password) =>
            runAction(() =>
              resetAdminAccountPassword(action.target!.id, { password }),
            )
          }
        />
      )}

      {action.kind === 'resetMfa' && action.target && (
        <ConfirmModal
          open
          title="Reset MFA for this admin?"
          description={`They will be required to re-enroll their authenticator app on next login. Any active session for ${action.target.username} will be revoked.`}
          confirmLabel="Reset MFA"
          cancelLabel="Cancel"
          danger
          busy={action.busy}
          onCancel={closeAction}
          onConfirm={() =>
            runAction(() => resetAdminAccountMfa(action.target!.id))
          }
        />
      )}

      {action.kind === 'toggleDisabled' && action.target && (
        <ConfirmModal
          open
          title={
            action.target.isDisabled
              ? `Re-enable ${action.target.username}?`
              : `Disable ${action.target.username}?`
          }
          description={
            action.target.isDisabled
              ? `${action.target.username} will be able to sign in again.`
              : `${action.target.username} will be signed out of all active sessions and blocked from logging in. The account is NOT deleted — you can re-enable it at any time.`
          }
          confirmLabel={action.target.isDisabled ? 'Enable' : 'Disable'}
          cancelLabel="Cancel"
          danger={!action.target.isDisabled}
          busy={action.busy}
          onCancel={closeAction}
          onConfirm={() =>
            runAction(() =>
              updateAdminAccount(action.target!.id, {
                isDisabled: !action.target!.isDisabled,
              }),
            )
          }
        />
      )}

      {action.kind === 'delete' && action.target && (
        <ConfirmModal
          open
          title="Delete this admin account?"
          description={`This is a hard delete of ${action.target.username}. This cannot be undone.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          danger
          busy={action.busy}
          onCancel={closeAction}
          onConfirm={() =>
            runAction(() => deleteAdminAccount(action.target!.id))
          }
        />
      )}

      {action.error && action.kind && action.kind !== 'create' && action.kind !== 'edit' && action.kind !== 'resetPassword' && (
        <div
          role="alert"
          data-testid="admin-users-action-error"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2"
        >
          {action.error}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row actions — disabled for the actor's own row so you can't demote/lock/
// delete yourself out of the system.
// ---------------------------------------------------------------------------

interface RowActionsProps {
  actor: { id: string } | null;
  row: AdminAccountListItem;
  onAction: (a: ActionState) => void;
}

function RowActions({ actor, row, onAction }: RowActionsProps) {
  const isSelf = actor?.id === row.id;

  const mkBtn = (
    label: string,
    kind: Exclude<ActionKind, null | 'create'>,
    testId: string,
    danger = false,
  ) => (
    <button
      type="button"
      disabled={isSelf}
      title={isSelf ? 'You cannot perform this action on yourself' : undefined}
      onClick={() =>
        onAction({ kind, target: row, busy: false, error: null })
      }
      data-testid={testId}
      className={[
        'text-xs rounded px-2 py-1 border',
        danger
          ? 'border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-40'
          : 'border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40',
      ].join(' ')}
    >
      {label}
    </button>
  );

  return (
    <div className="flex flex-wrap gap-2">
      {mkBtn('Edit', 'edit', `admin-users-edit-${row.id}`)}
      {/* One-click activate/deactivate — disabling revokes sessions + blocks
          login WITHOUT deleting the account, for any role (admin or content
          manager). Full hard-delete stays a separate, explicit action. */}
      {mkBtn(
        row.isDisabled ? 'Enable' : 'Disable',
        'toggleDisabled',
        `admin-users-toggle-disabled-${row.id}`,
        !row.isDisabled,
      )}
      {mkBtn(
        'Reset password',
        'resetPassword',
        `admin-users-reset-password-${row.id}`,
      )}
      {mkBtn('Reset MFA', 'resetMfa', `admin-users-reset-mfa-${row.id}`)}
      {mkBtn('Delete', 'delete', `admin-users-delete-${row.id}`, true)}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create modal
// ---------------------------------------------------------------------------

interface CreateAdminModalProps {
  busy: boolean;
  error: string | null;
  onSubmit: (body: {
    username: string;
    displayName: string;
    role: AdminUserRole;
    password: string;
  }) => void;
  onCancel: () => void;
}

function CreateAdminModal({
  busy,
  error,
  onSubmit,
  onCancel,
}: CreateAdminModalProps) {
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<AdminUserRole>('admin');
  const [password, setPassword] = useState('');

  const canSubmit =
    username.trim().length >= 3 &&
    displayName.trim().length >= 1 &&
    password.length >= ADMIN_PASSWORD_MIN_LENGTH;

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      username: username.trim(),
      displayName: displayName.trim(),
      role,
      password,
    });
  }

  return (
    <ModalShell
      title="Add admin user"
      testId="admin-users-create-modal"
      onCancel={onCancel}
      busy={busy}
    >
      <form onSubmit={handleSubmit} className="space-y-3">
        <ModalField label="Username">
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoFocus
            disabled={busy}
            data-testid="admin-users-create-username"
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </ModalField>
        <ModalField label="Display name">
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            disabled={busy}
            data-testid="admin-users-create-display-name"
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </ModalField>
        <ModalField label="Role">
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as AdminUserRole)}
            disabled={busy}
            data-testid="admin-users-create-role"
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
          >
            {ADMIN_ACCOUNT_ROLES.map((r) => (
              <option key={r} value={r}>
                {ADMIN_ROLE_LABELS[r]}
              </option>
            ))}
          </select>
        </ModalField>
        <ModalField
          label="Initial password"
          help={`At least ${ADMIN_PASSWORD_MIN_LENGTH} characters. The new admin will be prompted to enroll MFA on first login.`}
        >
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
            data-testid="admin-users-create-password"
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </ModalField>

        {error && <ModalError message={error} testId="admin-users-create-error" />}
        <ModalActions
          busy={busy}
          canSubmit={canSubmit}
          submitLabel="Create"
          submitTestId="admin-users-create-submit"
          onCancel={onCancel}
        />
      </form>
    </ModalShell>
  );
}

// ---------------------------------------------------------------------------
// Edit modal (role / displayName / isDisabled)
// ---------------------------------------------------------------------------

interface EditAdminModalProps {
  target: AdminAccountListItem;
  busy: boolean;
  error: string | null;
  onSubmit: (patch: {
    displayName?: string;
    role?: AdminUserRole;
    isDisabled?: boolean;
  }) => void;
  onCancel: () => void;
}

function EditAdminModal({
  target,
  busy,
  error,
  onSubmit,
  onCancel,
}: EditAdminModalProps) {
  const [displayName, setDisplayName] = useState(target.displayName);
  const [role, setRole] = useState<AdminUserRole>(target.role);
  const [isDisabled, setIsDisabled] = useState(target.isDisabled);

  const changed =
    displayName.trim() !== target.displayName ||
    role !== target.role ||
    isDisabled !== target.isDisabled;

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!changed) return;
    const patch: {
      displayName?: string;
      role?: AdminUserRole;
      isDisabled?: boolean;
    } = {};
    if (displayName.trim() !== target.displayName) {
      patch.displayName = displayName.trim();
    }
    if (role !== target.role) patch.role = role;
    if (isDisabled !== target.isDisabled) patch.isDisabled = isDisabled;
    onSubmit(patch);
  }

  return (
    <ModalShell
      title={`Edit ${target.username}`}
      testId="admin-users-edit-modal"
      onCancel={onCancel}
      busy={busy}
    >
      <form onSubmit={handleSubmit} className="space-y-3">
        <ModalField label="Display name">
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            disabled={busy}
            data-testid="admin-users-edit-display-name"
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </ModalField>
        <ModalField label="Role">
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as AdminUserRole)}
            disabled={busy}
            data-testid="admin-users-edit-role"
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
          >
            {ADMIN_ACCOUNT_ROLES.map((r) => (
              <option key={r} value={r}>
                {ADMIN_ROLE_LABELS[r]}
              </option>
            ))}
          </select>
        </ModalField>
        <ModalField label="Status">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isDisabled}
              onChange={(e) => setIsDisabled(e.target.checked)}
              disabled={busy}
              data-testid="admin-users-edit-disabled"
            />
            <span>Disabled (revoke sessions, block login)</span>
          </label>
        </ModalField>

        {error && <ModalError message={error} testId="admin-users-edit-error" />}
        <ModalActions
          busy={busy}
          canSubmit={changed}
          submitLabel="Save"
          submitTestId="admin-users-edit-submit"
          onCancel={onCancel}
        />
      </form>
    </ModalShell>
  );
}

// ---------------------------------------------------------------------------
// Reset password modal
// ---------------------------------------------------------------------------

interface ResetPasswordModalProps {
  target: AdminAccountListItem;
  busy: boolean;
  error: string | null;
  onSubmit: (password: string) => void;
  onCancel: () => void;
}

function ResetPasswordModal({
  target,
  busy,
  error,
  onSubmit,
  onCancel,
}: ResetPasswordModalProps) {
  const [password, setPassword] = useState('');
  const canSubmit = password.length >= ADMIN_PASSWORD_MIN_LENGTH;

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit(password);
  }

  return (
    <ModalShell
      title={`Reset password for ${target.username}`}
      testId="admin-users-reset-password-modal"
      onCancel={onCancel}
      busy={busy}
    >
      <form onSubmit={handleSubmit} className="space-y-3">
        <ModalField
          label="New password"
          help={`At least ${ADMIN_PASSWORD_MIN_LENGTH} characters. The admin's current session will be revoked.`}
        >
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            disabled={busy}
            data-testid="admin-users-reset-password-input"
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </ModalField>
        {error && (
          <ModalError
            message={error}
            testId="admin-users-reset-password-error"
          />
        )}
        <ModalActions
          busy={busy}
          canSubmit={canSubmit}
          submitLabel="Reset password"
          submitTestId="admin-users-reset-password-submit"
          onCancel={onCancel}
        />
      </form>
    </ModalShell>
  );
}

// ---------------------------------------------------------------------------
// Shared modal bits
// ---------------------------------------------------------------------------

interface ModalShellProps {
  title: string;
  testId: string;
  busy: boolean;
  onCancel: () => void;
  children: React.ReactNode;
}

function ModalShell({ title, testId, busy, onCancel, children }: ModalShellProps) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid={testId}
      className="fixed inset-0 z-40 flex items-center justify-center px-4"
    >
      <div
        className="absolute inset-0 bg-slate-900/50"
        onClick={() => {
          if (!busy) onCancel();
        }}
      />
      <div className="relative bg-white rounded-lg shadow-lg max-w-md w-full p-5 space-y-4">
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        {children}
      </div>
    </div>
  );
}

function ModalField({
  label,
  help,
  children,
}: {
  label: string;
  help?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1 text-sm">
      <span className="block text-xs font-medium text-slate-700">{label}</span>
      {children}
      {help && <span className="block text-xs text-slate-500">{help}</span>}
    </label>
  );
}

function ModalError({
  message,
  testId,
}: {
  message: string;
  testId: string;
}) {
  return (
    <div
      role="alert"
      data-testid={testId}
      className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2"
    >
      {message}
    </div>
  );
}

interface ModalActionsProps {
  busy: boolean;
  canSubmit: boolean;
  submitLabel: string;
  submitTestId: string;
  onCancel: () => void;
}

function ModalActions({
  busy,
  canSubmit,
  submitLabel,
  submitTestId,
  onCancel,
}: ModalActionsProps) {
  return (
    <div className="flex justify-end gap-2">
      <button
        type="button"
        onClick={onCancel}
        disabled={busy}
        className="text-sm border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        type="submit"
        disabled={busy || !canSubmit}
        data-testid={submitTestId}
        className="text-sm rounded bg-slate-800 text-white px-3 py-1.5 hover:bg-slate-700 disabled:opacity-50"
      >
        {busy ? 'Working…' : submitLabel}
      </button>
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
}
